import { useCan } from '@/hooks/useRbac';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useAuthState } from '@/utils/auth';
import { BilderitModuleId } from '@/generated/codegen';
import React from 'react';

export enum ReportType {
  DailyReport = 'DailyReport',
  MachineNotice = 'MachineNotice',
  FuelReport = 'FuelReport',
  StockReport = 'StockReport',
  Checklist = 'Checklist',
}

const ReportOptions = {
  DailyReport: {
    label: 'Parte Diario',
    value: ReportType.DailyReport,
  },
  MachineNotice: {
    label: 'Novedades',
    value: ReportType.MachineNotice,
  },
  FuelReport: {
    label: 'Despacho de combustible',
    value: ReportType.FuelReport,
  },
  StockReport: {
    label: 'Stock de combustible',
    value: ReportType.StockReport,
  },
  Checklist: {
    label: 'Checklists',
    value: ReportType.Checklist,
  },
};

/**
 * Whether the Checklists module is on, for the buttons/entry points that
 * have no machine (and so no company) in scope: a machinist has exactly one
 * company via `roles[0]` (same assumption `MachinistMachineHistoryQueries.tsx`
 * makes) — but a superroot's role has none. Without a company the module
 * gate can't be evaluated, so it never hides Checklist (a real "sin
 * checklists asignados" surfaces once a machine is chosen, where the gate
 * does have a company to check).
 */
export const useChecklistModuleEnabled = (): boolean => {
  const { user } = useAuthState();
  const companyId = Number(user?.roles?.[0]?.company?.id) || undefined;
  const { isFeatureEnabled } = useFeatureFlags(false, companyId);
  return !companyId || isFeatureEnabled(BilderitModuleId.Checklists);
};

export const useAvailableReportTypes = () => {
  const can = useCan();
  const checklistModuleEnabled = useChecklistModuleEnabled();
  return React.useMemo(() => {
    return Object.values(ReportOptions).filter(
      (option) =>
        can(`create-report:${option.value}`) &&
        (option.value !== ReportType.Checklist || checklistModuleEnabled)
    );
  }, [can, checklistModuleEnabled]);
};
